import { Navigations } from "@/AppRoutes";
import { scrollToTop } from "@/utils";
import { Music } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import HeaderFullScreenMenu from "./HeaderFullScreenMenu";
import LogoCompact from "./Logo";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled ? "bg-white shadow-lg" : "bg-white/95 backdrop-blur-sm"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-8 py-3.5 sm:py-4">
          <div className="flex items-center justify-between gap-6 xl:gap-10">
            <Link
              to="/"
              className="flex items-center shrink-0 group"
              onClick={() => scrollToTop()}
            >
              <LogoCompact />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-5 xl:space-x-8">
              {Navigations.filter(
                (item) => item.categories.includes(0) && item.id !== "archives",
              ).map((item) => {
                const isActive =
                  window.location.pathname === item.href ||
                  (item.href !== "/" &&
                    window.location.pathname.startsWith(item.href));
                const isHymns = item.href === "/chants-desperance";

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={
                      isHymns
                        ? "flex items-center"
                        : `text-gray-700 hover:text-church-blue-700 font-semibold text-xs xl:text-sm transition-colors duration-150 whitespace-nowrap flex items-center ${
                            isActive
                              ? "text-church-blue-800 font-bold border-b-2 border-church-blue-800 pb-0.5"
                              : ""
                          }`
                    }
                    onClick={() => scrollToTop()}
                    title={isHymns ? "Chants d'Espérance" : item.name}
                    aria-label={item.name}
                  >
                    {isHymns ? (
                      <span
                        className={`group/hymn relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-300 ${
                          isActive
                            ? "bg-gradient-to-r from-church-blue-800 via-church-blue-700 to-sky-700 text-white shadow-md shadow-church-blue-800/25 ring-2 ring-church-blue-300/60"
                            : "bg-slate-100 hover:bg-church-blue-50/80 text-slate-700 hover:text-church-blue-800 border border-slate-200/90 hover:border-church-blue-200 shadow-sm hover:shadow"
                        }`}
                      >
                        <span
                          className={`flex items-center justify-center w-6 h-6 rounded-full transition-transform duration-300 group-hover/hymn:scale-110 ${
                            isActive
                              ? "bg-white/20 text-amber-300"
                              : "bg-church-blue-100 text-church-blue-700"
                          }`}
                        >
                          <Music className="w-3.5 h-3.5" />
                        </span>
                        <span className="font-bold text-xs tracking-wider uppercase">
                          Chants
                        </span>
                        <span className="relative flex h-2 w-2">
                          <span
                            className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                              isActive ? "bg-amber-300" : "bg-sky-500"
                            }`}
                          ></span>
                          <span
                            className={`relative inline-flex rounded-full h-2 w-2 ${
                              isActive ? "bg-amber-400" : "bg-sky-500"
                            }`}
                          ></span>
                        </span>
                      </span>
                    ) : (
                      item.name
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Mobile Actions: Don & Hamburger Menu */}
            <div className="flex items-center gap-2 sm:gap-3.5 shrink-0">
              <a
                href="https://www.paypal.com/donate?hosted_button_id=LPCZP88RDTNK4"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 sm:gap-1.5 bg-church-blue-800 hover:bg-church-blue-900 text-white font-semibold px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm transition-all shadow-sm shrink-0"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
                </svg>
                <span>Don Paypal</span>
              </a>

              <button
                className="flex items-center space-x-1 sm:space-x-2 text-slate-700 hover:text-church-blue-800 font-medium text-xs sm:text-sm transition-colors duration-100 p-1 sm:p-2 rounded-lg shrink-0"
                onClick={() => setIsMenuOpen(true)}
                aria-label="Ouvrir le menu"
              >
                <span className="hidden xl:block">MENU</span>
                <div className="flex flex-col space-y-1">
                  <div className="w-5 h-0.5 bg-current transition-all duration-100"></div>
                  <div className="w-5 h-0.5 bg-current transition-all duration-100"></div>
                  <div className="w-5 h-0.5 bg-current transition-all duration-100"></div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Full Menu with right-to-left animation */}
      <div
        className={`fixed inset-0 z-50 transition-transform duration-500 ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ pointerEvents: isMenuOpen ? "auto" : "none" }}
      >
        <HeaderFullScreenMenu isOpen={isMenuOpen} onClose={handleMenuClose} />
      </div>
    </>
  );
};

export default Header;
